// src/pages/ContactPage.js
import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faPhone, 
  faEnvelope, 
  faClock 
} from '@fortawesome/free-solid-svg-icons';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    services: []
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [validated, setValidated] = useState(false);
  
  const services = [
    'Software Development',
    'AI/ML Solutions',
    'Cloud Services',
    'Blockchain Development',
    'IoT Applications',
    'Consulting Services',
    'Other'
  ];
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        services: [...formData.services, value]
      });
    } else {
      setFormData({
        ...formData,
        services: formData.services.filter(service => service !== value)
      });
    }
  };
  
  const handleSubmit = (e) => {
    const form = e.currentTarget;
    e.preventDefault();
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    // In a real application, you would handle form submission here
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setValidated(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      message: '',
      services: []
    });
  };
  
  return (
    <>
      {/* Hero Section */}
      <section className="bg-primary text-white py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-4 fw-bold mb-4">Contact Us</h1>
              <p className="lead">
                Have a question or ready to start your next project? We'd love to hear from you. 
                Reach out to our team for a consultation or more information about our services.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding">
        <Container>
          <Row>
            <Col lg={8} className="mb-5 mb-lg-0">
              <Card className="border-0 shadow">
                <Card.Body className="p-4 p-md-5">
                  <h2 className="mb-4">Get in Touch</h2>
                  
                  {submitted && (
                    <Alert variant="success" className="mb-4">
                      Thank you for your message! Our team will get back to you shortly.
                    </Alert>
                  )}
                  
                  <Form validated={validated} onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="formName">
                          <Form.Label>Full Name *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your name"
                          />
                          <Form.Control.Feedback type="invalid">
                            Please provide your name.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="formEmail">
                          <Form.Label>Email Address *</Form.Label>
                          <Form.Control
                            required
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Your email"
                          />
                          <Form.Control.Feedback type="invalid">
                            Please provide a valid email.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="formPhone">
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Your phone number"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="formCompany">
                          <Form.Label>Company</Form.Label>
                          <Form.Control
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your company"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-3" controlId="formSubject">
                      <Form.Label>Subject *</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Subject of your message"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a subject.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-4" controlId="formMessage">
                      <Form.Label>Message *</Form.Label>
                      <Form.Control
                        required
                        as="textarea"
                        rows={5}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a message.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <Form.Label>Services of Interest</Form.Label>
                      <div>
                        {services.map((service, index) => (
                          <Form.Check
                            key={index}
                            inline
                            label={service}
                            name="services"
                            type="checkbox"
                            id={`service-${index}`}
                            value={service}
                            checked={formData.services.includes(service)}
                            onChange={handleCheckboxChange}
                          />
                        ))}
                      </div>
                    </Form.Group>
                    
                    <Button type="submit" variant="primary" size="lg">
                      Send Message
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={4}>
              <Card className="border-0 shadow mb-4">
                <Card.Body className="p-4">
                  <h3 className="mb-4">Contact Information</h3>
                  
                  <div className="d-flex mb-4">
                    <div className="me-3 text-primary">
                      <FontAwesomeIcon icon={faMapMarkerAlt} size="lg" />
                    </div>
                    <div>
                      <h5 className="mb-1">Our Location</h5>
                      <p className="mb-0">
                        123 Tech Avenue<br />
                        San Francisco, CA 94103<br />
                        United States
                      </p>
                    </div>
                  </div>
                  
                  <div className="d-flex mb-4">
                    <div className="me-3 text-primary">
                      <FontAwesomeIcon icon={faPhone} size="lg" />
                    </div>
                    <div>
                      <h5 className="mb-1">Phone Number</h5>
                      <p className="mb-0">
                        <a href="tel:+1-555-123-4567" className="text-decoration-none">+1 (555) 123-4567</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="d-flex mb-4">
                    <div className="me-3 text-primary">
                      <FontAwesomeIcon icon={faEnvelope} size="lg" />
                    </div>
                    <div>
                      <h5 className="mb-1">Email Address</h5>
                      <p className="mb-0">
                        <a href="mailto:info@techinnovate.com" className="text-decoration-none">info@techinnovate.com</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="d-flex">
                    <div className="me-3 text-primary">
                      <FontAwesomeIcon icon={faClock} size="lg" />
                    </div>
                    <div>
                      <h5 className="mb-1">Business Hours</h5>
                      <p className="mb-0">
                        Monday - Friday: 9:00 AM - 6:00 PM<br />
                        Saturday - Sunday: Closed
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow">
                <Card.Body className="p-4">
                  <h3 className="mb-4">Global Offices</h3>
                  
                  <h5 className="mb-2">New York</h5>
                  <p className="mb-3">
                    456 Madison Ave<br />
                    New York, NY 10022
                  </p>
                  
                  <h5 className="mb-2">London</h5>
                  <p className="mb-3">
                    78 Chancery Lane<br />
                    London, WC2A 1QP
                  </p>
                  
                  <h5 className="mb-2">Singapore</h5>
                  <p className="mb-0">
                    10 Marina Boulevard<br />
                    Singapore 018983
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Map Section */}
      <section className="mb-5">
        <Container fluid className="px-0">
          <div className="map-container">
            {/* In a real implementation, you would integrate with Google Maps or another map service */}
            <div className="bg-light text-center py-5">
              <h4 className="mb-3">Interactive Map</h4>
              <p className="mb-0">Map integration would be displayed here in a real implementation</p>
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-light-custom">
        <Container>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Card className="border-0 shadow mb-4">
                <Card.Body>
                  <h5>What types of businesses do you work with?</h5>
                  <p className="mb-0">
                    We work with businesses of all sizes across various industries, from startups to large enterprises. Our clients range from energy and healthcare companies to financial institutions and technology firms.
                  </p>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow mb-4">
                <Card.Body>
                  <h5>How long does a typical project take?</h5>
                  <p className="mb-0">
                    Project timelines vary depending on scope and complexity. A simple web application might take 2-3 months, while a comprehensive enterprise solution could take 6-12 months. We'll provide a detailed timeline during the planning phase.
                  </p>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow mb-4">
                <Card.Body>
                  <h5>What is your pricing model?</h5>
                  <p className="mb-0">
                    We offer flexible pricing models including fixed-price, time and materials, and retainer arrangements. The appropriate model depends on your project requirements and preferences, which we'll discuss during consultation.
                  </p>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow">
                <Card.Body>
                  <h5>How do you handle project management and communication?</h5>
                  <p className="mb-0">
                    We use agile methodologies with regular sprints and check-ins. Each client is assigned a dedicated project manager who serves as your main point of contact. We maintain transparent communication through weekly progress reports and a shared project management platform.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default ContactPage;